import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api, ApiError } from '../../../lib/api-client';
import { useToastStore } from '../../../stores/toast-store';
import { ModalBase } from '../../supervisor/components/modals/ModalBase';
import { resolvePlaybookName } from '../hooks/use-playbook-name-map';
import { playbookTransitionsQueryKey } from '../hooks/use-playbook-transitions';

import type { PlaybookTransitionProposalWire } from '../../../lib/api-client';

export interface PlaybookTransitionReviewModalProps {
  proposal: PlaybookTransitionProposalWire | null;
  /** Mapa id→name resolvido por `usePlaybookNameMap` (fonte honesta única). */
  playbookNames: Map<string, string>;
  onClose: () => void;
}

/**
 * PlaybookTransitionReviewModal — S7 PR3 (S6 Condução Governada, Q-J6).
 *
 * Molde 1:1 de `ProposalReviewModal.tsx` (painel-v3): mostra o que a IA
 * PROPÕE (from→to playbook + `reason`, TODOS vindos do backend, nunca
 * fabricados — o Blocker recorrente desta frente foi copy inventada) e
 * oferece Confirmar (`POST :id/confirm`) / Rejeitar (`POST :id/reject`,
 * motivo opcional em textarea, igual ao decline do mold).
 *
 * 409 (`proposal-not-pending`/`conversation-terminal`/
 * `proposal-stale-playbook-changed`/`conversation-closed-during-confirm`) e
 * 422 (`target-playbook-inactive`) usam a `message` HONESTA que o backend já
 * devolve em PT-BR (`ConflictException`/`UnprocessableEntityException`
 * construídas com `message` pronta para o humano — não reformulamos) +
 * refetch da lista (o estado real pode ter mudado sob o operador).
 */
export function PlaybookTransitionReviewModal({
  proposal,
  playbookNames,
  onClose,
}: PlaybookTransitionReviewModalProps): React.JSX.Element | null {
  const push = useToastStore((s) => s.push);
  const queryClient = useQueryClient();

  const [isPending, setIsPending] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  if (proposal === null) return null;

  const p = proposal;
  const confidencePct = Math.round(p.confidence * 100);
  const fromName = resolvePlaybookName(p.fromPlaybookId, playbookNames);
  const toName = resolvePlaybookName(p.toPlaybookId, playbookNames);

  function handleClose(): void {
    setRejectReason('');
    setShowRejectInput(false);
    setFailed(null);
    onClose();
  }

  function invalidateAfterDecision(): void {
    void queryClient.invalidateQueries({
      queryKey: playbookTransitionsQueryKey(p.conversationId),
    });
    void queryClient.invalidateQueries({ queryKey: ['conversation', p.conversationId] });
    void queryClient.invalidateQueries({ queryKey: ['conversations'] });
  }

  async function handleConfirm(): Promise<void> {
    setIsPending(true);
    setFailed(null);
    try {
      await api.confirmPlaybookTransition(p.id);
      invalidateAfterDecision();
      push({
        variant: 'success',
        title: `Transição confirmada — playbook trocado para ${toName}.`,
      });
      handleClose();
    } catch (err) {
      // Backend já devolve `message` honesta (PT-BR, pronta pro humano) nos
      // 409/422 desta rota — não reformulamos.
      const message = err instanceof ApiError ? err.message : 'Não foi possível confirmar.';
      setFailed(message);
      invalidateAfterDecision();
    } finally {
      setIsPending(false);
    }
  }

  async function handleReject(): Promise<void> {
    setIsPending(true);
    setFailed(null);
    try {
      const reason = rejectReason.trim() !== '' ? rejectReason.trim() : undefined;
      await api.rejectPlaybookTransition(p.id, reason);
      invalidateAfterDecision();
      push({ variant: 'success', title: 'Transição rejeitada. Playbook atual mantido.' });
      handleClose();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Não foi possível rejeitar.';
      setFailed(message);
      invalidateAfterDecision();
    } finally {
      setIsPending(false);
    }
  }

  return (
    <ModalBase
      open
      onClose={handleClose}
      title="Proposta de troca de playbook"
      width={520}
      footer={
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button
            className="btn-ghost-mini"
            type="button"
            onClick={handleClose}
            disabled={isPending}
          >
            Cancelar
          </button>
          <button
            className="btn-ghost-mini"
            type="button"
            disabled={isPending}
            onClick={() => {
              if (showRejectInput) {
                void handleReject();
              } else {
                setShowRejectInput(true);
              }
            }}
          >
            {isPending && showRejectInput ? 'Rejeitando…' : 'Rejeitar'}
          </button>
          <button
            className="btn-ghost-mini btn-ghost-mini--primary"
            type="button"
            disabled={isPending || showRejectInput}
            onClick={() => void handleConfirm()}
          >
            {isPending && !showRejectInput ? 'Confirmando…' : 'Confirmar'}
          </button>
        </div>
      }
    >
      <div className="modal-form">
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-2)', marginBottom: 4 }}>
          A IA propõe trocar o playbook desta conversa:
        </p>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--fw-semibold)',
            marginBottom: 'var(--space-3)',
          }}
        >
          <span data-testid="playbook-transition-from">{fromName}</span>
          <span aria-hidden="true">→</span>
          <span data-testid="playbook-transition-to" style={{ color: 'var(--brand)' }}>
            {toName}
          </span>
        </div>

        <div
          style={{
            background: 'var(--bg-2)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-3)',
            marginBottom: 'var(--space-3)',
          }}
        >
          <p
            style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--fg-1)',
              margin: 0,
              lineHeight: 1.5,
            }}
            data-testid="playbook-transition-reason"
          >
            {p.reason}
          </p>
        </div>

        <p
          style={{
            fontSize: 'var(--text-xs)',
            color: 'var(--fg-3)',
            marginBottom: 'var(--space-3)',
          }}
        >
          Confiança da IA: {confidencePct}%
        </p>

        {showRejectInput && (
          <label className="modal-form__field">
            <span className="modal-form__label">Motivo da rejeição (opcional)</span>
            <textarea
              className="modal-form__textarea"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={2}
              placeholder="Ex.: o assunto ainda é o mesmo, a IA se confundiu…"
              autoFocus
            />
          </label>
        )}

        {failed !== null && (
          <div className="banner banner--danger" role="alert">
            <div className="banner__body">
              <span className="banner__title">{failed}</span>
            </div>
          </div>
        )}
      </div>
    </ModalBase>
  );
}
